// Every row, header, footer and empty-state sentence of the `{Slicer.` menu, built from
// the board's slicers and their live values. The help popover's Slicers group reads the
// same rows, so the two never disagree on a value or a token.

import { kindWord, variablesForControls } from "./resolveTemplate.mjs";
import { completeSlicerToken, slicerToken } from "./slicerTokenTrigger.mjs";

export { variablesForControls };

export const KIND_LABELS = { date: "Dates", subject: "Athletes & drills" };

/** What each variable means, for the help popover. */
export const VARIABLE_MEANINGS = {
  StartDate: "First day of the window",
  EndDate: "Last day of the window",
  DateRange: "The slicer's range label",
  DayFilter: "Day filter tags",
  Subjects: "Athlete, position and group names",
  SubjectCount: "Number of subjects picked",
  Athlete: "First athlete in the picker",
  Drills: "Drill names",
};

const BLANK_TEXT = {
  DayFilter: "blank (no day filter)",
  Drills: "blank (no drills picked)",
  Athlete: "blank (no athlete picked)",
};

const NOT_AVAILABLE = "not available yet";

/** "Dates", "Athletes & drills", or both joined with a middle dot for a legacy multi-kind slicer. */
export function kindLabel(controls) {
  const labels = [];
  if (controls?.some((c) => c === "date" || c === "single_date")) labels.push(KIND_LABELS.date);
  if (controls?.some((c) => c === "subject" || c === "single_player")) labels.push(KIND_LABELS.subject);
  return labels.join(" · ");
}

function lowerFirst(text) {
  return text ? text[0].toLowerCase() + text.slice(1) : text;
}

/** The text a variable row shows for its live value, and whether it reads as blank. */
export function variableText(name, value, values) {
  if (value == null) {
    // A session window has no calendar start before the engine answers (phase 2).
    if (name === "StartDate" && typeof values?.DateRange === "string" && values.DateRange) {
      return { text: `first of the ${lowerFirst(values.DateRange)}`, blank: true };
    }
    return { text: NOT_AVAILABLE, blank: true };
  }
  if (value === "" && BLANK_TEXT[name]) return { text: BLANK_TEXT[name], blank: true };
  return { text: value, blank: false };
}

/**
 * One row per slicer, in board order. `slicers` are `{ key, label, controls, color }`;
 * `values` is the `resolveTemplate` map keyed by the same keys. Each row carries the
 * variables its kind offers with the live value, the canonical token and its meaning.
 */
export function slicerMenuRows(slicers, values) {
  return (slicers ?? []).map((slicer) => {
    const controls = slicer.controls ?? [];
    const live = values?.[slicer.key]?.values ?? {};
    const variables = variablesForControls(controls).map((name) => ({
      name,
      token: slicerToken(slicer.key, name),
      meaning: VARIABLE_MEANINGS[name],
      ...variableText(name, Object.prototype.hasOwnProperty.call(live, name) ? live[name] : null, live),
    }));
    return {
      key: slicer.key,
      label: slicer.label ?? "",
      color: slicer.color,
      controls,
      kindWord: kindWord(controls),
      kindLabel: kindLabel(controls),
      variables,
    };
  });
}

/** The slicer rows whose key or label starts with the partial, without regard to case. */
export function filterSlicerRows(rows, partial) {
  const needle = (partial ?? "").toLowerCase();
  if (!needle) return rows;
  return rows.filter(
    (row) => row.key.toLowerCase().startsWith(needle) || row.label.toLowerCase().startsWith(needle),
  );
}

/** The variable rows whose name starts with the partial, without regard to case. */
export function filterVariableRows(variables, partial) {
  const needle = (partial ?? "").toLowerCase();
  if (!needle) return variables;
  return variables.filter((variable) => variable.name.toLowerCase().startsWith(needle));
}

function emptyModel(level, text, detail = null) {
  return { level, header: null, items: [{ kind: "empty", text, detail }], pickable: 0, footer: null };
}

function footerFor(row) {
  if (row.kindWord === "date") {
    return {
      text: "Add a format before the closing brace: ",
      example: `{Slicer.${row.key}.StartDate:MMM D}`,
      tail: ". Shift+Enter leaves the caret there.",
    };
  }
  return {
    text: "Cap a list before the closing brace: ",
    example: `{Slicer.${row.key}.Subjects:1}`,
    tail: ". Shift+Enter leaves the caret there.",
  };
}

/**
 * The menu for an open trigger (`{ level, key, partial }`) over the board's rows.
 *
 * Level 1 lists the slicers; level 2 lists the chosen slicer's variables under a header
 * row, with a footer about the format slot. An empty list holds one disabled row that
 * says why. `pickable` is the number of rows Enter, Tab or a click can pick.
 */
export function buildMenuModel(trigger, rows) {
  if (trigger.level === 1) {
    if (!rows.length) return emptyModel(1, "No slicers on this board. Add a Slicer widget first.");
    const matches = filterSlicerRows(rows, trigger.partial);
    if (!matches.length) return emptyModel(1, `No slicer matches "${trigger.partial}"`);
    return {
      level: 1,
      header: null,
      items: matches.map((row) => ({
        kind: "slicer",
        id: row.key,
        key: row.key,
        label: row.label,
        color: row.color,
        kindLabel: row.kindLabel,
        text: completeSlicerToken(row.key),
      })),
      pickable: matches.length,
      footer: null,
    };
  }

  const key = (trigger.key ?? "").toUpperCase();
  const row = rows.find((candidate) => candidate.key.toUpperCase() === key);
  if (!row) return emptyModel(2, `No slicer has key ${key}`, "Delete back to {Slicer. to pick from the list.");

  const header = { key: row.key, label: row.label, color: row.color, kindLabel: row.kindLabel };
  const matches = filterVariableRows(row.variables, trigger.partial);
  if (!matches.length) {
    const names = row.variables.map((variable) => variable.name).join(", ");
    return {
      level: 2,
      header,
      items: [{ kind: "empty", text: `${row.key} is a ${row.kindWord} slicer. Its variables: ${names}.`, detail: null }],
      pickable: 0,
      footer: null,
    };
  }
  return {
    level: 2,
    header,
    items: matches.map((variable) => ({
      kind: "variable",
      id: variable.name,
      name: variable.name,
      valueText: variable.text,
      blank: variable.blank,
      text: variable.token,
      formatText: slicerToken(row.key, variable.name, true),
    })),
    pickable: matches.length,
    footer: footerFor(row),
  };
}
